import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Menu, X, Phone, User as UserIcon, LogOut, LayoutDashboard, Wrench, MapPin, Shield, ChevronDown, Heart } from "lucide-react";
import { useFavorites } from "@/hooks/use-favorites";
import { ForumNotificationsBell } from "@/components/ForumNotificationsBell";

type NavItem =
  | { to: string; label: string; children?: undefined }
  | { to: string; label: string; children: ReadonlyArray<{ to: string; params?: Record<string, string>; label: string; desc?: string }> };

const rapidoChildren = [
  { to: "/rapido", label: "Översikt Rapido", desc: "Hela modellprogrammet & hub" },
  { to: "/rapido/begagnade", label: "Begagnade Rapido", desc: "Live-lager från Fyndanu" },
  { to: "/rapido/prislista", label: "Prislista 2024–2025", desc: "Nya Rapido från fabrik" },
  { to: "/rapido/aterforsaljare", label: "Återförsäljare", desc: "Officiell partner i norra Sverige" },
  { to: "/rapido/modeller/$model", params: { model: "v55" }, label: "Rapido V55", desc: "Kompakt plåtis" },
  { to: "/rapido/modeller/$model", params: { model: "v62" }, label: "Rapido V62", desc: "Plåtis – fransk säng" },
  { to: "/rapido/modeller/$model", params: { model: "v68" }, label: "Rapido V68", desc: "Plåtis – enkelsängar" },
  { to: "/rapido/modeller/$model", params: { model: "866f" }, label: "Rapido 866F", desc: "Halvintegrerad" },
  { to: "/rapido/modeller/$model", params: { model: "9005-dfh" }, label: "Rapido 9005 DFH", desc: "Integrerad flaggskepp" },
  { to: "/rapido/modeller/$model", params: { model: "platis" }, label: "Rapido Plåtis", desc: "Guide till van-serien" },
] as const;

const fordonChildren = [
  { to: "/fordon", label: "Alla fordon", desc: "Hela lagret – live" },
  { to: "/husbil-matchning", label: "Husbil-matchning", desc: "6 frågor · träffprocent på lagret" },
  { to: "/husbilar-till-salu", label: "Husbilar till salu", desc: "Filtrera märke, pris, körkort" },
  { to: "/husvagnar-till-salu", label: "Husvagnar till salu", desc: "Kabe, Polar, Adria m.fl." },
  { to: "/rapido/begagnade", label: "Begagnade Rapido", desc: "Vårt Rapido-lager" },
  { to: "/vardera-husbil", label: "Värdera din husbil", desc: "Gratis värdering – hela räkningen syns" },
] as const;

const guiderChildren = [
  { to: "/kunskap", label: "Kunskapsbanken", desc: "Fördjupningar från verkstaden" },
  { to: "/guider", label: "Alla guider", desc: "Startsidan för kunskapsbanken" },
  { to: "/stallplatser-husbil", label: "Ställplatser i Sverige", desc: "Priser, regler, vinteröppet" },
  { to: "/solceller-husbil", label: "Solceller & litium", desc: "Elbehov, utbyte, priser monterat" },
  { to: "/fukttest-husbil", label: "Fukttest steg för steg", desc: "Gränsvärden & 12 mätpunkter" },
  { to: "/b96-korkort", label: "B96-körkort", desc: "Viktgränser, kostnad, prov" },
  { to: "/husbilsforsakring", label: "Husbilsförsäkring", desc: "Premier, självrisk, villkor" },
  { to: "/leasa-husbil", label: "Leasa husbil", desc: "Leasing, avbetalning & momsen" },
  { to: "/besiktning-husbil", label: "Besiktning av husbil", desc: "Intervall, priser, egenkontroll" },
  { to: "/dieselvarmare-husbil", label: "Dieselvärmare", desc: "Förbrukning ned till −25 °C" },
  { to: "/husbilsdack", label: "Husbilsdäck", desc: "CP-däck, lufttryck, DOT-kod" },
  { to: "/salja-husbil", label: "Sälja husbil", desc: "Förmedling, inbyte eller privat" },
  { to: "/tvatta-husvagn", label: "Tvätta husvagn rätt", desc: "Metoden inför varje leverans" },
  { to: "/tester", label: "Långtidstester", desc: "Egna mätvärden från verkstaden" },
] as const;

const verktygChildren = [
  {
    to: "/verktyg/passar-den-hos-mig",
    label: "Passar den hos mig?",
    desc: "Nytt · planvy, svängen in & AR i mobilen",
  },
  { to: "/verktyg/fuktprotokoll", label: "Fuktprotokoll", desc: "Nytt · 12 mätpunkter & gränsvärden" },
  { to: "/vardera-husbil", label: "Värdera din husbil", desc: "Gratis – hela räkningen syns" },
  { to: "/prisstatistik-husbil", label: "Prisstatistik", desc: "Medianpriser per märke & segment" },
  { to: "/prisindex", label: "AK Prisindex", desc: "Prisutveckling månad för månad" },
  { to: "/borsen", label: "Fritidsbörsen", desc: "Bolagen bakom märkena, kurs & utveckling" },
  { to: "/husbil-matchning", label: "Husbil-matchning", desc: "6 frågor · träffprocent" },
  { to: "/husbil-resa", label: "Reseplaneraren", desc: "Bygg din husbilsrutt" },
  { to: "/jamfor", label: "Jämför modeller", desc: "Sida mot sida" },
  { to: "/fakta", label: "Fakta & mått", desc: "Tekniska specar" },
] as const;

const communityChildren = [
  { to: "/forum", label: "Fritidsforum", desc: "Frågor & svar med andra ägare" },
  { to: "/blogg", label: "Bloggen", desc: "Nyheter, tester och reportage" },
  { to: "/campingtips", label: "Campingtips", desc: "Platser tipsade av medlemmar" },
  { to: "/erfarenheter", label: "Erfarenhetsrapporter", desc: "Verkliga ägarberättelser" },
] as const;

const marknadChildren = [
  { to: "/marknad", label: "Marknadsrummet", desc: "Allt marknadsdata samlat" },
  { to: "/prisindex", label: "AK Prisindex", desc: "Prisutveckling månad för månad" },
  { to: "/prisstatistik-husbil", label: "Prisstatistik", desc: "Median per segment & fabrikat" },
  { to: "/borsen", label: "Fritidsbörsen", desc: "Bolagen bakom märkena" },
  { to: "/salda", label: "Sålda fordon", desc: "Verkliga slutpriser" },
  { to: "/vardera-husbil", label: "Värdera din husbil", desc: "Gratis – hela räkningen syns" },
] as const;

const reservdelarChildren = [
  { to: "/reservdelar", label: "Beställ reservdel", desc: "Fota delen – vi tar fram rätt artikel" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "fonster-och-luckor" }, label: "Fönster & luckor", desc: "Akrylrutor, gångjärn, hakar" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "kyl-och-frys" }, label: "Kyl & frys", desc: "Dometic, Thetford, kylaggregat" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "varme-och-vatten" }, label: "Värme & vatten", desc: "Truma, pumpar, boiler" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "gasol" }, label: "Gasolsystem", desc: "Regulator, slang, spis" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "el-och-solpanel" }, label: "El & solpanel", desc: "Laddare, batteri, panel" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "kaross" }, label: "Kaross & list", desc: "Lister, luckor, tätning" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "belysning" }, label: "Belysning", desc: "Lampor, LED, bakljus" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "inredning" }, label: "Inredning", desc: "Lås, gångjärn, textil" },
  { to: "/reservdelar/kategori/$slug", params: { slug: "chassi-och-broms" }, label: "Chassi & broms", desc: "Bromsbackar, stödben" },
  { to: "/reservdelar/$brand", params: { brand: "rapido" }, label: "Rapido reservdelar", desc: "Officiell återförsäljare" },
  { to: "/reservdelar/$brand", params: { brand: "kabe" }, label: "Kabe reservdelar", desc: "Delar & typskylt" },
] as const;

const foretagChildren = [
  { to: "/om-oss", label: "Om oss", desc: "Verkstaden i Kalix sedan 2005" },
  { to: "/rapido", label: "Rapido – återförsäljare", desc: "Hela modellprogrammet & prislista" },
  { to: "/butik", label: "Butik – tillbehör", desc: "Fuktmätare, gasol, solpanel" },
  { to: "/fritid", label: "Fritid & tillbehör", desc: "Butiken på plats" },
  { to: "/boka-service", label: "Boka service", desc: "Verkstadstid & fuktkontroll" },
  { to: "/kontakt", label: "Kontakt & öppettider", desc: "Ring, mejla eller kom in" },
] as const;


const navItems: ReadonlyArray<NavItem> = [
  { to: "/fordon", label: "Fordon", children: fordonChildren },
  { to: "/reservdelar", label: "Reservdelar", children: reservdelarChildren },
  { to: "/guider", label: "Guider", children: guiderChildren },
  { to: "/vardera-husbil", label: "Verktyg", children: verktygChildren },
  { to: "/marknad", label: "Marknad", children: marknadChildren },
  { to: "/forum", label: "Community", children: communityChildren },
  { to: "/om-oss", label: "Om oss", children: foretagChildren },
];



export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<Record<string, boolean>>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();

  useEffect(() => {
    async function load(session: any) {
      const s = session ?? (await supabase.auth.getSession()).data.session;
      setSignedIn(!!s);
      setEmail(s?.user?.email ?? null);
      if (s?.user?.id) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("avatar_url")
          .eq("id", s.user.id)
          .maybeSingle();
        if (prof?.avatar_url) {
          if (prof.avatar_url.startsWith("http")) setAvatarUrl(prof.avatar_url);
          else {
            const { data } = await supabase.storage.from("user-content").createSignedUrl(prof.avatar_url, 60 * 60);
            setAvatarUrl(data?.signedUrl ?? null);
          }
        } else setAvatarUrl(null);
      } else setAvatarUrl(null);
    }
    load(null);
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => load(s));
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Stäng mobilmenyn vid navigering
  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [pathname]);

  // Lås bakgrundsscroll när mobilmenyn är öppen
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  async function signOut() {
    setMenuOpen(false);
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    toast.success("Utloggad");
    navigate({ to: "/", replace: true });
  }

  const initial = (email ?? "?").slice(0, 1).toUpperCase();

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "border-b border-border bg-background/90 backdrop-blur-md" : "border-b border-transparent bg-background"
      }`}
    >
      <div className="h-1 w-full bg-primary" />
      <div className="container-edge flex items-center justify-between py-4">
        <Link to="/" className="group flex items-baseline gap-2">
          <span className="font-display text-xl leading-none tracking-tight md:text-2xl">AK Husbil</span>
          <span className="font-italic-display text-lg leading-none text-accent md:text-xl">&amp; Fritid</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.to} className="group relative">
                <Link
                  to={item.to}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-foreground/80 transition hover:bg-secondary hover:text-foreground"
                  activeProps={{ className: "inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium bg-secondary text-foreground" }}
                >
                  {item.label}
                  <ChevronDown size={13} className="transition group-hover:rotate-180" />
                </Link>
                <div className="invisible absolute left-1/2 top-full z-50 w-[520px] -translate-x-1/2 pt-3 opacity-0 transition group-hover:visible group-hover:opacity-100">
                  <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {item.children.map((c) => (
                        <Link
                          key={`${c.to}-${JSON.stringify(c.params ?? {})}`}
                          to={c.to as any}
                          params={c.params as any}
                          className="rounded-xl px-3 py-2.5 text-sm transition hover:bg-secondary"
                        >
                          <div className="font-medium text-foreground">{c.label}</div>
                          {c.desc && <div className="mt-0.5 text-xs text-muted-foreground">{c.desc}</div>}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-full px-3 py-2 text-sm font-medium text-foreground/80 transition hover:bg-secondary hover:text-foreground"
                activeProps={{ className: "rounded-full px-3 py-2 text-sm font-medium bg-secondary text-foreground" }}
                activeOptions={{ exact: item.to === "/" }}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-foreground/80 transition hover:text-foreground">
            <Phone size={15} /> 070-862 97 43
          </a>
          <FavoritesHeaderLink />
          {signedIn && <ForumNotificationsBell />}
          {signedIn ? (
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-border bg-card p-1 pr-3 transition hover:bg-secondary"
                aria-label="Mitt konto"
              >
                <span className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-primary text-sm font-medium text-primary-foreground">
                  {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : initial}
                </span>
                <span className="text-sm">Mitt konto</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                  <MenuItem to="/konto" icon={<LayoutDashboard size={14} />} label="Översikt" onClick={() => setMenuOpen(false)} />
                  <MenuItem to="/konto/profil" icon={<UserIcon size={14} />} label="Profil" onClick={() => setMenuOpen(false)} />
                  <MenuItem to="/konto/mina-forfragningar" icon={<Wrench size={14} />} label="Mina reservdelar" onClick={() => setMenuOpen(false)} />
                  <MenuItem to="/konto/mina-tips" icon={<MapPin size={14} />} label="Mina campingtips" onClick={() => setMenuOpen(false)} />
                  <MenuItem to="/konto/sakerhet" icon={<Shield size={14} />} label="Säkerhet" onClick={() => setMenuOpen(false)} />
                  <button
                    onClick={signOut}
                    className="flex w-full items-center gap-2 border-t border-border px-4 py-2.5 text-left text-sm text-foreground/80 hover:bg-secondary"
                  >
                    <LogOut size={14} /> Logga ut
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
              Logga in
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href="tel:0708629743"
            aria-label="Ring 070-862 97 43"
            className="grid h-10 w-10 place-items-center rounded-full bg-primary text-primary-foreground"
          >
            <Phone size={16} />
          </a>
          <FavoritesHeaderLink />
          {signedIn && <ForumNotificationsBell />}
        </div>

        <button
          aria-label="Meny"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full border border-border bg-card p-2 text-foreground lg:hidden"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain border-t border-border bg-background lg:hidden">
          <nav className="container-edge flex flex-col py-4 pb-28">
            {navItems.map((item) => {
              const isExpanded = !!expandedMobile[item.to];
              return (
                <div key={item.to} className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="block min-w-0 truncate py-3 font-display text-xl"
                      activeProps={{ className: "block py-3 font-display text-2xl text-accent" }}
                      activeOptions={{ exact: item.to === "/" }}
                    >
                      {item.label}
                    </Link>
                    {item.children && (
                      <button
                        type="button"
                        onClick={() => setExpandedMobile((prev) => ({ ...prev, [item.to]: !prev[item.to] }))}
                        aria-label={isExpanded ? "Dölj undermeny" : "Visa undermeny"}
                        className="grid h-10 w-10 place-items-center rounded-full transition hover:bg-secondary"
                      >
                        <ChevronDown
                          size={20}
                          className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                  </div>
                  {item.children && isExpanded && (
                    <div className="flex flex-col pb-2 pl-3">
                      {item.children.map((c) => (
                        <Link
                          key={`${c.to}-${JSON.stringify(c.params ?? {})}`}
                          to={c.to as any}
                          params={c.params as any}
                          onClick={() => setOpen(false)}
                          className="py-2 text-sm text-foreground/75"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {signedIn ? (
              <>
                <Link to="/konto" onClick={() => setOpen(false)} className="mt-4 rounded-full bg-primary px-4 py-3 text-center text-sm font-medium text-primary-foreground">
                  Mitt konto
                </Link>
                <button onClick={() => { setOpen(false); signOut(); }} className="mt-2 rounded-full border border-border px-4 py-3 text-center text-sm">
                  Logga ut
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="mt-4 rounded-full bg-primary px-4 py-3 text-center text-sm font-medium text-primary-foreground">
                Logga in
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function MenuItem({
  to, icon, label, onClick,
}: { to: string; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <Link
      to={to as any}
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground/85 transition hover:bg-secondary hover:text-foreground"
    >
      {icon} {label}
    </Link>
  );
}

function FavoritesHeaderLink() {
  const { count } = useFavorites();
  return (
    <Link
      to="/sparade"
      aria-label={`Sparade fordon${count ? ` (${count})` : ""}`}
      className="relative inline-flex items-center justify-center rounded-full border border-border bg-card p-2 text-foreground/80 transition hover:bg-secondary"
    >
      <Heart size={16} className={count > 0 ? "fill-red-500 text-red-500" : ""} />
      {count > 0 && (
        <span className="absolute -right-1 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
          {count}
        </span>
      )}
    </Link>
  );
}
